import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import api from "../../utils/apiConfig";
import logger from '../../utils/logger';
import AuroraBackground from "../../components/AuroraBackground";
import BreathingStars from "../../components/BreathingStars";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { login, user, isLoading } = useAuth();
  const { isDarkMode, toggleDarkMode, theme } = useTheme();

  // Debug current auth state
  useEffect(() => {
    logger.info("Current auth state:", {
      user,
      isLoading,
      token: localStorage.getItem("token"),
      refreshToken: localStorage.getItem("refreshToken"),
    });
  }, [user, isLoading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Debug initial state
    logger.info("=== Starting login process ===");
    logger.info("API Configuration:", {
      baseURL: api.defaults.baseURL,
      headers: api.defaults.headers,
      withCredentials: api.defaults.withCredentials,
    });

    try {
      // Debug request
      logger.info("Sending login request:", {
        url: `${api.defaults.baseURL}/auth/login`,
        email,
        headers: api.defaults.headers,
      });

      const response = await api.post("/auth/login", {
        email,
        password,
      });

      // Debug full response
      logger.info("Raw login response:", {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        data: response.data,
      });

      // Check for response data and success
      if (!response.data?.success) {
        logger.error("Login failed:", response.data?.message);
        throw new Error(response.data?.message || "Login failed");
      }

      // Extract user data from nested response
      const { token, refreshToken, user: userData } = response.data.data;

      if (!token) {
        logger.error("Missing token in response:", response.data);
        throw new Error("Login failed - no token in response");
      }

      // Debug extracted data
      logger.info("Extracted login data:", {
        hasToken: !!token,
        hasRefreshToken: !!refreshToken,
        userData: {
          ...userData,
          password: undefined,
        },
      });

      // Use server-provided values or safely construct them with proper capitalization
      const firstName = (userData.firstName || "").trim();
      const lastName = (userData.lastName || "").trim();

      // Capitalize first letter of each name part
      const formatName = (str) =>
        str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

      const formattedFirstName = firstName ? formatName(firstName) : "";
      const formattedLastName = lastName ? formatName(lastName) : "";

      // Use formatted names or fall back to email username with capitalization
      const name =
        formattedFirstName && formattedLastName
          ? `${formattedFirstName} ${formattedLastName}`
          : formatName(email.split("@")[0]);

      // Create advisor letter from formatted names or email
      const advisorLetter =
        formattedFirstName && formattedLastName
          ? `${formattedFirstName[0]}${formattedLastName[0]}`
          : email.charAt(0).toUpperCase();

      // Prepare login data with formatted names
      const loginData = {
        token,
        refreshToken,
        user: {
          ...userData,
          firstName: formattedFirstName || formatName(email.split("@")[0]),
          lastName: formattedLastName,
          advisorLetter,
        },
      };

      // Debug login data
      logger.info("Prepared login data:", {
        ...loginData,
        token: token ? "[PRESENT]" : "[MISSING]",
        refreshToken: refreshToken ? "[PRESENT]" : "[MISSING]",
        user: {
          ...loginData.user,
          firstName: loginData.user.firstName,
          lastName: loginData.user.lastName,
          advisorLetter: loginData.user.advisorLetter,
        },
      });

      // Call login and wait for state to update
      await login(loginData);

      // Verify auth state with retries
      let attempts = 0;
      const maxAttempts = 5;
      const delay = 200;

      while (attempts < maxAttempts) {
        const storedToken = localStorage.getItem("token");
        const storedRefreshToken = localStorage.getItem("refreshToken");
        
        if (storedToken && storedRefreshToken) {
          logger.info("Login successful, navigating to home dashboard...");
          navigate("/home-dashboard", { replace: true });
          return;
        }
        
        logger.info("Waiting for tokens to be stored:", {
          attempt: attempts + 1,
          hasToken: !!storedToken,
          hasRefreshToken: !!storedRefreshToken
        });
        
        await new Promise((resolve) => setTimeout(resolve, delay));
        attempts++;
      }

      throw new Error(
        "Login failed: Tokens not properly stored after multiple attempts"
      );
    } catch (error) {
      // Detailed error logging
      logger.error("Login error:", {
        message: error.message,
        response: {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
        },
        request: {
          url: error.config?.url,
          baseURL: error.config?.baseURL,
          method: error.config?.method,
          headers: error.config?.headers,
        },
      });

      if (!error.response) {
        setError(
          "Unable to connect to the server. Please ensure the server is running and try again.",
        );
      } else if (error.response.status === 401) {
        setError("Invalid email or password");
      } else {
        setError(
          error.response?.data?.message || "An unexpected error occurred",
        );
      }
    }
  };

  // Effect to handle navigation after successful login
  useEffect(() => {
    // Only navigate if we have a real user (not demo) and valid token
    if (user && !user.isDemo && localStorage.getItem("token")) {
      navigate("/home-dashboard", { replace: true });
    }
  }, [user, navigate]);

  if (isLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-black' : 'bg-white'} transition-colors duration-200`}>
        {isDarkMode ? <BreathingStars /> : <AuroraBackground />}
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className={`relative min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-black' : 'bg-white'} overflow-hidden transition-colors duration-200`}>
      {isDarkMode ? <BreathingStars /> : <AuroraBackground />}
      
      <div className="relative flex items-center space-x-16 p-8 z-10">
        <div className="flex-shrink-0 transition-transform duration-300 hover:scale-105">
          <img
            src={isDarkMode ? "/images/logos/q-logo-white.png" : "/images/logos/q-logo-color.png"}
            alt="Retirement Systems"
            className={`w-80 md:w-96 h-auto ${isDarkMode ? 'opacity-90 drop-shadow-[0_0_15px_rgba(255,255,255,0.2)]' : 'opacity-100'} transition-all duration-300`}
          />
        </div>

        <div className="w-96">
          <div className={`p-8 ${isDarkMode ? 'backdrop-blur-xl' : ''}`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                Log in
              </h2>
              <button
                onClick={toggleDarkMode}
                className={`p-2 rounded-full transition-all duration-300 ${
                  isDarkMode 
                    ? 'bg-neutral-800 text-gray-300 hover:bg-neutral-700 hover:shadow-[0_0_15px_rgba(48,255,200,0.2)]' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:shadow-[0_0_15px_rgba(40,180,110,0.2)]'
                }`}
              >
                {isDarkMode ? '🌙' : '☀️'}
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div
                  className={`px-4 py-3 rounded-lg relative ${
                    isDarkMode 
                      ? 'bg-red-900/20 border border-red-800 text-red-200' 
                      : 'bg-red-100 border border-red-400 text-red-700'
                  }`}
                  role="alert"
                >
                  <span className="block sm:inline">{error}</span>
                </div>
              )}

              <div>
                <label
                  htmlFor="email"
                  className={`block text-sm font-medium ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}
                >
                  Email:
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`mt-1 block w-full px-4 py-2 rounded-full shadow-sm focus:outline-none transition-all duration-200 ${
                    isDarkMode 
                      ? 'bg-neutral-800/80 border border-neutral-600 text-gray-200 placeholder-gray-500 focus:ring-[#03b93b] focus:border-[#03b93b] focus:shadow-[0_0_10px_rgba(3,185,59,0.2)]' 
                      : 'bg-white/80 border-gray-300 text-gray-900 focus:ring-[#029831] focus:border-[#029831] focus:shadow-[0_0_10px_rgba(2,152,49,0.2)]'
                  }`}
                />
              </div>

              <div>
                <label
                  htmlFor="password"
                  className={`block text-sm font-medium ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}
                >
                  Password:
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`mt-1 block w-full px-4 py-2 rounded-full shadow-sm focus:outline-none transition-all duration-200 ${
                    isDarkMode 
                      ? 'bg-neutral-800/80 border border-neutral-600 text-gray-200 placeholder-gray-500 focus:ring-[#03b93b] focus:border-[#03b93b] focus:shadow-[0_0_10px_rgba(3,185,59,0.2)]' 
                      : 'bg-white/80 border-gray-300 text-gray-900 focus:ring-[#029831] focus:border-[#029831] focus:shadow-[0_0_10px_rgba(2,152,49,0.2)]'
                  }`}
                />
              </div>

              <div>
                <button
                  type="submit"
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-[#029831] hover:bg-[#027a28] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#029831] transition-all duration-300 hover:shadow-[0_0_15px_rgba(2,152,49,0.3)]"
                >
                  Log in
                </button>
              </div>

              <div className="flex justify-between text-sm">
                <Link
                  to="/forgot-password"
                  className={`relative group ${
                    isDarkMode 
                      ? 'text-[#03b93b] hover:text-[#029831]' 
                      : 'text-[#029831] hover:text-[#018529]'
                  } transition-colors duration-200`}
                >
                  <span>Forgot your password?</span>
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-current transition-all duration-300 group-hover:w-full"></span>
                </Link>
                <Link
                  to="/register"
                  className={`relative group ${
                    isDarkMode 
                      ? 'text-[#03b93b] hover:text-[#029831]' 
                      : 'text-[#029831] hover:text-[#018529]'
                  } transition-colors duration-200`}
                >
                  <span>Create an account</span>
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-current transition-all duration-300 group-hover:w-full"></span>
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
